import {
  getProcess,
  resolveLatestProcessName,
  BOOKING_PROCESS_NAME,
  NEGOTIATION_PROCESS_NAME,
  PURCHASE_PROCESS_NAME,
} from '../transactions/transaction';
import { denormalisedResponseEntities } from './data';

/**
 * «Нужна реакция» (D34, D37): whose move is it in this transaction, and what does the row say?
 *
 * One resolver (getInboxMove) for every surface: the inbox row status, dot and fill, the
 * sidebar «Нужна реакция» tab and count, the mobile-menu count and the topbar dots.
 *
 * D37: the status says whose move it is — the viewer's (an imperative), the counterparty's
 * («Ждём …») or nobody's (a state phrase; final states keep their per-state text). It never
 * names the counterparty's role: on task listings (default-inquiry) the specialist who writes
 * is the tx customer.
 * 1. A deleted or banned counterparty in a non-final state: «Собеседник недоступен», nobody's
 *    move, «Архив».
 * 2. Inquiry states: the loaded messages decide (last from the counterparty → your move; from
 *    you → theirs; none → the recipient's move).
 * 3. Every other state: the next transition that advances the deal (process.edn); operator-only
 *    and automatic ones are nobody's move.
 *
 * Works on a denormalised transaction (tx.provider, tx.customer, tx.messages[].sender).
 */

// D37: one translation key per status.
const STATUS = {
  replyToRequest: 'InboxPage.status.replyToRequest',
  replyToMessage: 'InboxPage.status.replyToMessage',
  sendOffer: 'InboxPage.status.sendOffer',
  reviewOffer: 'InboxPage.status.reviewOffer',
  reviewChanges: 'InboxPage.status.reviewChanges',
  payOrder: 'InboxPage.status.payOrder',
  payBooking: 'InboxPage.status.payBooking',
  confirmBooking: 'InboxPage.status.confirmBooking',
  deliverWork: 'InboxPage.status.deliverWork',
  acceptWork: 'InboxPage.status.acceptWork',
  makeChanges: 'InboxPage.status.makeChanges',
  leaveReview: 'InboxPage.status.leaveReview',
  waitingForReply: 'InboxPage.status.waitingForReply',
  waitingForOffer: 'InboxPage.status.waitingForOffer',
  waitingForDecision: 'InboxPage.status.waitingForDecision',
  waitingForPayment: 'InboxPage.status.waitingForPayment',
  waitingForConfirmation: 'InboxPage.status.waitingForConfirmation',
  waitingForDelivery: 'InboxPage.status.waitingForDelivery',
  waitingForAcceptance: 'InboxPage.status.waitingForAcceptance',
  waitingForChanges: 'InboxPage.status.waitingForChanges',
  waitingForReview: 'InboxPage.status.waitingForReview',
  bookingConfirmed: 'InboxPage.status.bookingConfirmed',
  disputeUnderReview: 'InboxPage.status.disputeUnderReview',
  userUnavailable: 'InboxPage.status.userUnavailable',
};

const VIEWER_STATUSES = [
  STATUS.replyToRequest,
  STATUS.replyToMessage,
  STATUS.sendOffer,
  STATUS.reviewOffer,
  STATUS.reviewChanges,
  STATUS.payOrder,
  STATUS.payBooking,
  STATUS.confirmBooking,
  STATUS.deliverWork,
  STATUS.acceptWork,
  STATUS.makeChanges,
  STATUS.leaveReview,
];

const COUNTERPARTY_STATUSES = [
  STATUS.waitingForReply,
  STATUS.waitingForOffer,
  STATUS.waitingForDecision,
  STATUS.waitingForPayment,
  STATUS.waitingForConfirmation,
  STATUS.waitingForDelivery,
  STATUS.waitingForAcceptance,
  STATUS.waitingForChanges,
  STATUS.waitingForReview,
];

// D37 tables: [customer, provider] status per non-inquiry, non-final state. Paid processes:
// the client is the tx customer, the specialist the tx provider. A state without a row
// (purchase «received», left by a timer on entry) is nobody's move with its per-state text.
const MOVE_STATUSES = {
  [NEGOTIATION_PROCESS_NAME]: {
    'quote-requested': [STATUS.waitingForOffer, STATUS.sendOffer],
    'offer-pending': [STATUS.reviewOffer, STATUS.waitingForDecision],
    'update-pending': [STATUS.reviewChanges, STATUS.waitingForDecision],
    'customer-offer-pending': [STATUS.waitingForDecision, STATUS.reviewOffer],
    'pending-payment': [STATUS.payOrder, STATUS.waitingForPayment],
    'offer-accepted': [STATUS.waitingForDelivery, STATUS.deliverWork],
    delivered: [STATUS.acceptWork, STATUS.waitingForAcceptance],
    'changes-requested': [STATUS.waitingForChanges, STATUS.makeChanges],
    completed: [STATUS.leaveReview, STATUS.leaveReview],
    'reviewed-by-customer': [STATUS.waitingForReview, STATUS.leaveReview],
    'reviewed-by-provider': [STATUS.leaveReview, STATUS.waitingForReview],
  },
  [BOOKING_PROCESS_NAME]: {
    'pending-payment': [STATUS.payBooking, STATUS.waitingForPayment],
    preauthorized: [STATUS.waitingForConfirmation, STATUS.confirmBooking],
    accepted: [STATUS.bookingConfirmed, STATUS.bookingConfirmed],
    delivered: [STATUS.leaveReview, STATUS.leaveReview],
    'reviewed-by-customer': [STATUS.waitingForReview, STATUS.leaveReview],
    'reviewed-by-provider': [STATUS.leaveReview, STATUS.waitingForReview],
  },
  [PURCHASE_PROCESS_NAME]: {
    'pending-payment': [STATUS.payOrder, STATUS.waitingForPayment],
    purchased: [STATUS.waitingForDelivery, STATUS.deliverWork],
    delivered: [STATUS.acceptWork, STATUS.waitingForAcceptance],
    disputed: [STATUS.disputeUnderReview, STATUS.disputeUnderReview],
    completed: [STATUS.leaveReview, STATUS.leaveReview],
    'reviewed-by-customer': [STATUS.waitingForReview, STATUS.leaveReview],
    'reviewed-by-provider': [STATUS.leaveReview, STATUS.waitingForReview],
  },
};

// Who started an inquiry: the actor of the process's initial inquiry transition (process.edn).
// default-negotiation `inquire` is the provider's; purchase / booking `inquire` and
// default-inquiry `inquire-without-payment` are the customer's.
const INQUIRY_INITIATOR_ROLE = { [NEGOTIATION_PROCESS_NAME]: 'provider' };

const getWhoseMove = statusId =>
  VIEWER_STATUSES.includes(statusId)
    ? 'viewer'
    : COUNTERPARTY_STATUSES.includes(statusId)
    ? 'counterparty'
    : 'none';

/**
 * Inquiry state names across the supported processes (purchase / booking / negotiation
 * «inquiry», default-inquiry «free-inquiry»), for API state filters.
 */
export const INQUIRY_STATE_NAMES = ['inquiry', 'free-inquiry'];

const getViewerRole = (tx, viewerId) => {
  const viewerUuid = viewerId?.uuid || viewerId;
  if (!viewerUuid) {
    return null;
  }
  if (tx?.provider?.id?.uuid === viewerUuid) {
    return 'provider';
  }
  if (tx?.customer?.id?.uuid === viewerUuid) {
    return 'customer';
  }
  return null;
};

const getTxProcessAndState = tx => {
  try {
    const process = getProcess(resolveLatestProcessName(tx?.attributes?.processName));
    return { process, state: process.getState(tx) };
  } catch (e) {
    return { process: null, state: null };
  }
};

/**
 * Is the transaction in an inquiry state of its process (purchase/booking/negotiation
 * «inquiry», default-inquiry «free-inquiry»)?
 */
export const isInquiryState = tx => {
  const { process, state } = getTxProcessAndState(tx);
  const inquiryStates = [process?.states?.INQUIRY, process?.states?.FREE_INQUIRY].filter(Boolean);
  return !!state && inquiryStates.includes(state);
};

const messageTime = message => {
  const createdAt = message?.attributes?.createdAt;
  return createdAt ? new Date(createdAt).getTime() : 0;
};

/**
 * The newest message among the ones included in the transaction. Included messages come
 * oldest-first and are capped (100 by default), so for a longer chat the newest one may be
 * missing: callers fetch it separately and pass it as `lastMessage` (see isViewerMove).
 */
export const getLastMessage = tx =>
  (tx?.messages || []).reduce(
    (latest, message) => (messageTime(message) >= messageTime(latest) ? message : latest),
    null
  );

const senderIdOf = message => message?.sender?.id?.uuid;

// D37 inquiry rule on the loaded messages (no tx role involved, except who started it). A
// message without a sender cannot be attributed and is ignored.
const getInquiryStatus = (tx, role, viewerUuid, lastMessageOverride) => {
  const loadedMessages = (tx?.messages || []).filter(senderIdOf);
  const lastMessage = senderIdOf(lastMessageOverride)
    ? lastMessageOverride
    : getLastMessage({ messages: loadedMessages });
  if (!lastMessage) {
    const processName = resolveLatestProcessName(tx?.attributes?.processName);
    const initiatorRole = INQUIRY_INITIATOR_ROLE[processName] || 'customer';
    return role === initiatorRole ? STATUS.waitingForReply : STATUS.replyToRequest;
  }
  if (senderIdOf(lastMessage) === viewerUuid) {
    return STATUS.waitingForReply;
  }
  const hasViewerWritten = loadedMessages.some(message => senderIdOf(message) === viewerUuid);
  return hasViewerWritten ? STATUS.replyToMessage : STATUS.replyToRequest;
};

/**
 * D37: whose move it is and the status the row shows.
 *
 * @param {Object} tx denormalised transaction
 * @param {UUID|string} viewerId current user id
 * @param {Object} [options]
 * @param {Object} [options.lastMessage] the newest message when the include is capped
 * @returns {{ whoseMove: 'viewer'|'counterparty'|'none', statusId: string|null, isArchived: boolean }}
 *   statusId is a translation key; isArchived: a final state or an unavailable counterparty («Архив»)
 */
export const getInboxMove = (tx, viewerId, options = {}) => {
  const role = getViewerRole(tx, viewerId);
  const { process, state } = getTxProcessAndState(tx);
  if (!role || !process || !state) {
    return { whoseMove: 'none', statusId: null, isArchived: false };
  }

  const processName = resolveLatestProcessName(tx.attributes.processName);
  const stateStatusId = `InboxPage.${processName}.${state}.status`;
  const isInquiry = isInquiryState(tx);
  // Final = no transition out of the state (free-inquiry has none either, but it is an inquiry).
  const isFinal = !isInquiry && Object.keys(process.graph.states[state]?.on || {}).length === 0;
  if (isFinal) {
    return { whoseMove: 'none', statusId: stateStatusId, isArchived: true };
  }

  const counterparty = role === 'provider' ? tx.customer : tx.provider;
  if (counterparty?.attributes?.deleted || counterparty?.attributes?.banned) {
    return { whoseMove: 'none', statusId: STATUS.userUnavailable, isArchived: true };
  }

  const viewerUuid = viewerId?.uuid || viewerId;
  const statusId = isInquiry
    ? getInquiryStatus(tx, role, viewerUuid, options.lastMessage)
    : MOVE_STATUSES[processName]?.[state]?.[(role === 'customer' ? 0 : 1)] || stateStatusId;
  return { whoseMove: getWhoseMove(statusId), statusId, isArchived: false };
};

/**
 * Is it the viewer's move? Derived from getInboxMove, so the status and the dot always agree.
 *
 * @param {Object} tx denormalised transaction
 * @param {UUID|string} viewerId current user id
 * @param {Object} [options]
 * @param {Object} [options.lastMessage] the newest message when the include is capped
 * @returns {boolean}
 */
export const isViewerMove = (tx, viewerId, options = {}) =>
  getInboxMove(tx, viewerId, options).whoseMove === 'viewer';

/**
 * Non-inquiry states in which the given tx role can have a move (D37 tables), for the API
 * state filters of the notification counts. Inquiry states have their own count query.
 *
 * @param {'customer'|'provider'} txRole
 * @returns {Array<string>} state names
 */
export const getMoveStateNames = txRole => {
  const roleIndex = txRole === 'customer' ? 0 : 1;
  const stateNames = Object.values(MOVE_STATUSES).flatMap(statusesByState =>
    Object.keys(statusesByState).filter(state =>
      VIEWER_STATUSES.includes(statusesByState[state][roleIndex])
    )
  );
  return [...new Set(stateNames)];
};

/**
 * Number of included messages that means the include may have been capped (API default 100).
 */
export const MESSAGE_INCLUDE_LIMIT = 100;

/**
 * Inquiry transactions whose included messages may miss the newest one.
 */
export const getCappedInquiryTransactions = txs =>
  (txs || []).filter(
    tx => isInquiryState(tx) && (tx?.messages || []).length >= MESSAGE_INCLUDE_LIMIT
  );

/**
 * Fetch the newest message (with its sender) for every capped inquiry chat: one request
 * per such chat, none otherwise. Messages query returns newest first.
 *
 * @returns {Promise<Object>} { [txUuid]: denormalised message }
 */
export const fetchLastMessagesForCappedInquiries = (sdk, txs) => {
  const capped = getCappedInquiryTransactions(txs);
  return Promise.all(
    capped.map(tx =>
      sdk.messages
        .query({ transaction_id: tx.id, include: ['sender'], page: 1, perPage: 1 })
        .then(response => [tx.id.uuid, denormalisedResponseEntities(response)[0] || null])
        .catch(() => [tx.id.uuid, null])
    )
  ).then(entries => entries.reduce((map, [txId, message]) => ({ ...map, [txId]: message }), {}));
};

/**
 * Count the transactions where it is the viewer's move.
 */
export const countViewerMoves = (txs, viewerId, lastMessageOverrides = {}) =>
  (txs || []).filter(tx =>
    isViewerMove(tx, viewerId, { lastMessage: lastMessageOverrides[(tx?.id?.uuid)] })
  ).length;
